import type { ComponentType } from 'react'
import { Icons } from '@/components/ui/icons'
import { canSee, type PageId } from '@/lib/access'
import type { PersonaId } from '@/lib/persona'
import { breadcrumbTrail, crumbLabelFor, type Crumb } from '@/lib/nav-paths'

export { crumbLabelsVersion, isNavItemActive, setCrumbLabel, subscribeCrumbLabels } from '@/lib/nav-paths'

/**
 * Product name in the rail and the title (PHASE-2 P1, plans/09 4.1). Presentation only, so an env switch;
 * the default mirrors Settings `brand.product_name`.
 */
export const AUTO_NAME = process.env.NEXT_PUBLIC_AUTO_NAME || 'Disaster Management AI Office'

/** Sub-label under the product name in the rail (mirrors Settings `brand.agency_name`). */
export const AUTO_SUBLABEL = process.env.NEXT_PUBLIC_AUTO_SUBLABEL || 'Disaster Management Agency'

/**
 * A live count on a nav entry, keyed like `GET /api/dr/nav-badges`
 * (plans/03 A3). A missing key means no badge; a badge never shows 0.
 */
export type NavBadgeKey = 'console' | 'workbench' | 'globe' | 'agencycalls' | 'checks' | 'depots' | 'deliveries'

export interface NavItem {
  id: PageId
  href: string
  label: string
  /** Japanese label shown before `label` in `lang="ja"` (plans/09 P9.5): 司令室 for the console. */
  labelJa?: string
  /** Extra words the command palette matches ("console", "home"). */
  keywords?: string[]
  icon: ComponentType<{ className?: string; strokeWidth?: number }>
  badge?: NavBadgeKey
}

/** The landing page's name (PHASE-2 P3): 司令室 in Noto Sans JP, then the romaji. */
export const CONSOLE_LABEL_JA = '司令室'
export const CONSOLE_LABEL = 'Shireishitsu'
export const CONSOLE_TEXT = `${CONSOLE_LABEL_JA} ${CONSOLE_LABEL}`

/** The plain text of a nav label ("司令室 Shireishitsu"), for titles, aria labels and search. */
export function navItemText(item: Pick<NavItem, 'label' | 'labelJa'>): string {
  return item.labelJa ? `${item.labelJa} ${item.label}` : item.label
}

export interface NavSection {
  title: string
  items: NavItem[]
}

/**
 * One list for the rail, the mobile sheet, the palette and the breadcrumb
 * (plans/00 5.3, plans/03 A5.1). Order per PHASE-2 P4: ask first, then the
 * 司令室, the Auto Workbench directly under it, then the story pages and relief.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Operations',
    items: [
      { id: 'ask', href: '/ask', label: 'Ask the AI Office', icon: Icons.sparkles },
      {
        id: 'console',
        href: '/',
        label: CONSOLE_LABEL,
        labelJa: CONSOLE_LABEL_JA,
        icon: Icons.dashboard,
        badge: 'console',
        keywords: ['console', 'home', 'shirei'],
      },
      { id: 'workbench', href: '/workbench', label: 'Auto Workbench', icon: Icons.checkCircle, badge: 'workbench' },
      { id: 'timeline', href: '/timeline', label: 'Incident Timeline', icon: Icons.history },
      { id: 'globe', href: '/globe', label: 'Operations Globe', icon: Icons.globe, badge: 'globe' },
      { id: 'agencycalls', href: '/agencies', label: 'Agencies and Calls', icon: Icons.phone, badge: 'agencycalls' },
      { id: 'checks', href: '/checks', label: 'Impact Checks', icon: Icons.clipboardCheck, badge: 'checks' },
      { id: 'depots', href: '/supplies', label: 'Relief Supplies', icon: Icons.warehouse, badge: 'depots' },
      { id: 'deliveries', href: '/deliveries', label: 'Relief Deliveries', icon: Icons.truck, badge: 'deliveries' },
    ],
  },
  {
    title: 'Auto Intelligence',
    items: [
      { id: 'policies', href: '/ai/policies', label: 'Auto Policies', icon: Icons.brain },
      { id: 'insights', href: '/ai/insights', label: 'Operational Insights', icon: Icons.lightbulb },
      { id: 'graph', href: '/ai/graph', label: 'AutoGraph', icon: Icons.network },
      { id: 'hub', href: '/ai-coworker-hub', label: 'AI Coworker Hub', icon: Icons.bot },
    ],
  },
  {
    title: 'Governance',
    items: [{ id: 'audit', href: '/audit', label: 'Governed Actions and Audit', icon: Icons.shieldCheck }],
  },
  {
    title: 'Platform',
    items: [
      { id: 'data', href: '/data-manager', label: 'Data Manager', icon: Icons.database },
      { id: 'presenter', href: '/presenter', label: 'Presenter', icon: Icons.presentation },
    ],
  },
  {
    title: 'Administration',
    items: [{ id: 'access', href: '/admin/access', label: 'Users and Access', icon: Icons.userCog }],
  },
]

/**
 * Labels of pages that are not in the rail (plans/00 5.3 hidden routes), so
 * guards and crumbs name them the same way.
 */
const HIDDEN_PAGE_LABELS: Partial<Record<PageId, string>> = {
  receipt: 'Receipt and shortfall form',
  checkin: 'Shelter check-in form',
}

/** Sections filtered to what `persona` may open; empty sections are dropped. */
export function visibleNavSections(persona: PersonaId): NavSection[] {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => canSee(persona, item.id)),
  })).filter((section) => section.items.length > 0)
}

/** The nav label of a page id (rail items and the hidden pages), as plain text. */
export function labelForPage(id: PageId): string {
  for (const section of NAV_SECTIONS) {
    const item = section.items.find((entry) => entry.id === id)
    if (item) return navItemText(item)
  }
  return HIDDEN_PAGE_LABELS[id] ?? id
}

/** The nav label for an exact href as plain text, or undefined when no entry has that href. */
export function labelForPath(path: string): string | undefined {
  for (const section of NAV_SECTIONS) {
    const item = section.items.find((entry) => entry.href === path)
    if (item) return navItemText(item)
  }
  return undefined
}

/** The root crumb's label. */
export const ROOT_LABEL = CONSOLE_TEXT

/** Header breadcrumb with labels taken from the navigation. */
export function breadcrumbsFor(pathname: string | null | undefined): Crumb[] {
  // The receipt form is a hidden route under Relief Deliveries: "Relief Deliveries / Receipt and shortfall form",
  // not the destination id the path carries.
  if (pathname && (pathname === '/receipt' || pathname.startsWith('/receipt/'))) {
    return [
      { label: labelForPage('deliveries'), href: '/deliveries' },
      { label: labelForPage('receipt'), href: pathname },
    ]
  }
  return breadcrumbTrail(pathname, (href) => crumbLabelFor(href) ?? labelForPath(href), ROOT_LABEL)
}

/** Badge text, or '' when there is nothing to show (a badge never shows 0). */
export function badgeText(count: number | undefined): string {
  if (count === undefined || count <= 0) return ''
  return count > 99 ? '99+' : String(count)
}

/** The Workbench badge counts decisions waiting on this persona, so it uses the Needs-you coral. */
export function isNeedsBadge(key: NavBadgeKey | undefined): boolean {
  return key === 'workbench'
}

/** What a badge count means, for screen readers: "Auto Workbench, 2 waiting for a decision". */
export function badgeSpoken(key: NavBadgeKey | undefined, count: string): string {
  return isNeedsBadge(key) ? `${count} waiting for a decision` : `${count} to look at`
}
